import { useState } from "react"
import { useRouter } from "next/router"

import {
  mapCreateTestSetInputToTransport,
  useCreateTestSetTransport,
} from "@/test-set/transport"
import { SplitButton, toast } from "@coval/ui"
import { useQueryClient } from "@tanstack/react-query"
import { Hash, MessageSquare, Sparkles } from "lucide-react"

import { invalidateHomeQueries } from "@/lib/home-query-cache"
import { logger } from "@/lib/logger"
import {
  enumerateFlowPaths,
  validateIVRFlow,
} from "@/components/ivr-flow/ivr-flow-utils"
import { useQueryParams } from "@/hooks/useQueryParams"
import type { IVRFlow } from "@/types/IVRFlow"
import { validateDtmfDigits, type ScriptTurn } from "@/types/ScriptTurn"
import type { TestCaseInput } from "@/types/TestCaseInput"
import { TestSetType } from "@/types/TestSetInput"

const log = logger("GenerateTestSetFromIVRButton")

type ScriptMode = "dtmf" | "text"

interface GenerateTestSetFromIVRButtonProps {
  flow: IVRFlow | null | undefined
  /** Used to name the generated test set. */
  contextLabel?: string | null
}

/** Encode a caller input as the right script-turn type. In `"dtmf"` mode,
 * keypad inputs (digits, *, #) become DTMF turns the persona dials and
 * spoken keywords (fuzzy edges like "operator") stay as text turns. In
 * `"text"` mode every turn is a bare text string regardless of shape —
 * useful for voice-only agents where the persona speaks digits aloud
 * rather than pressing keys. */
const toScriptTurn = (input: string, mode: ScriptMode): ScriptTurn => {
  if (mode === "dtmf" && validateDtmfDigits(input).error === null) {
    return { type: "dtmf", digits: input }
  }
  return input
}

const buildTestCasesFromFlow = (
  flow: IVRFlow,
  mode: ScriptMode,
): TestCaseInput[] => {
  const paths = enumerateFlowPaths(flow)
  return paths.map((path) => {
    const description = `IVR path: ${path.nodeIds.join(" → ")}`
    return {
      input_str: description,
      description,
      expected_behaviors: [],
      s3_key: null,
      s3_bucket: null,
      input_type: TestSetType.SCRIPT,
      script_turns: path.scriptTurns.map((turn) => toScriptTurn(turn, mode)),
      expected_output_json: {
        path: path.nodeIds,
      },
    }
  })
}

const buildTestSetDescription = (
  flow: IVRFlow,
  caseCount: number,
  mode: ScriptMode,
): string => {
  const terminalCount = flow.nodes.filter((n) => n.terminal).length
  const turnsLine =
    mode === "dtmf"
      ? `Each test case is a SCRIPT replaying the caller inputs along one path — keypad inputs as DTMF turns, spoken keywords as text.`
      : `Each test case is a SCRIPT replaying the caller inputs along one path as text turns — the persona speaks every input aloud, including digits.`
  return [
    `IVR flow test set generated from ${flow.nodes.length} node${flow.nodes.length === 1 ? "" : "s"} and ${flow.edges.length} edge${flow.edges.length === 1 ? "" : "s"}.`,
    `Covers ${caseCount} distinct path${caseCount === 1 ? "" : "s"} from "${flow.start_node}" to ${terminalCount} terminal node${terminalCount === 1 ? "" : "s"}.`,
    `${turnsLine} The visited node ids are stored in expected_output_json.path.`,
  ].join(" ")
}

const MODE_LABEL: Record<ScriptMode, string> = {
  dtmf: "DTMF",
  text: "text",
}

export const GenerateTestSetFromIVRButton = ({
  flow,
  contextLabel,
}: GenerateTestSetFromIVRButtonProps) => {
  const router = useRouter()
  const queryParams = useQueryParams()
  const queryClient = useQueryClient()
  const createTestSetMutation = useCreateTestSetTransport()

  const [isGenerating, setIsGenerating] = useState(false)

  const validation = flow
    ? validateIVRFlow(flow)
    : { valid: false, issues: ["No IVR flow defined."] }
  const paths = flow && validation.valid ? enumerateFlowPaths(flow) : []
  const canGenerate = !!flow && validation.valid && paths.length > 0

  const handleGenerate = async (mode: ScriptMode) => {
    if (!flow || !canGenerate) {
      toast.danger({
        title: "Cannot generate",
        description: validation.issues[0] ?? "No paths to test.",
      })
      return
    }

    setIsGenerating(true)
    try {
      const testCases = buildTestCasesFromFlow(flow, mode)
      const baseName = contextLabel
        ? `IVR Test Set – ${contextLabel}`
        : "IVR Test Set"
      const displayName = `${baseName} (${MODE_LABEL[mode]})`

      const transportResult = await createTestSetMutation.mutateAsync(
        mapCreateTestSetInputToTransport({
          testSetInput: {
            display_name: displayName,
            description: buildTestSetDescription(flow, testCases.length, mode),
            test_set_type: TestSetType.SCRIPT,
            testCases,
            parameters: {},
          },
        }),
      )

      if (!transportResult.success || !transportResult.test_set_id) {
        throw new Error("Failed to create test set")
      }
      const newTestSetId = transportResult.test_set_id

      await invalidateHomeQueries(queryClient)

      toast.success({
        title: "Test set generated",
        description: `${testCases.length} test case${testCases.length === 1 ? "" : "s"} created.`,
      })

      const slug =
        typeof router.query.slug === "string" ? router.query.slug : ""
      const href = queryParams.getHref(`/test-sets/${newTestSetId}`, slug)
      const url = new URL(href, window.location.origin)
      url.searchParams.set("new", "true")
      void router.push(url.pathname + url.search)
    } catch (error) {
      log.error("Failed to generate IVR test set", error)
      toast.error({
        title: "Generation failed",
        error,
        fallback: "Could not create test set from IVR flow.",
      })
    } finally {
      setIsGenerating(false)
    }
  }

  const label = (() => {
    if (isGenerating) {
      return "Generating…"
    }
    if (!flow) {
      return "Generate test set"
    }
    if (!validation.valid) {
      return "Fix IVR flow to generate"
    }
    return `Generate DTMF test set (${paths.length} path${paths.length === 1 ? "" : "s"})`
  })()

  return (
    <SplitButton
      variant="secondary"
      disabled={!canGenerate || isGenerating}
      onClick={() => {
        void handleGenerate("dtmf")
      }}
      aria-label="More generate options"
      items={[
        {
          label: "Generate DTMF script test set",
          icon: <Hash className="w-4 h-4" />,
          onClick: () => {
            void handleGenerate("dtmf")
          },
          disabled: !canGenerate || isGenerating,
        },
        {
          label: "Generate text script test set",
          icon: <MessageSquare className="w-4 h-4" />,
          onClick: () => {
            void handleGenerate("text")
          },
          disabled: !canGenerate || isGenerating,
        },
      ]}
    >
      <Sparkles className="w-4 h-4 mr-1" />
      {label}
    </SplitButton>
  )
}
